// src/components/ListDetail.js

import React, { useEffect, useState } from 'react';
import api from '../api';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Save,
  Cancel,
  Done,
  Undo,
  DragIndicator,
  DriveFileMove,
  ContentCopy,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack'; // For notifications
import LinkifiedText from './LinkifiedText';
import LoadingState from './LoadingState';

// @hello-pangea/dnd only starts a touch drag after a ~120ms press-and-hold, and
// cancels it if the finger moves first, so a normal swipe on the handle does
// nothing. This sensor starts the drag as soon as the drag handle is touched
// (the handle has touch-action: none, so the page doesn't scroll instead).
//
// The library's built-in touch sensor takes the drag lock on touchstart, so ours
// must run first: window listeners fire in registration order, hence the single
// listener registered at module load, before any DragDropContext mounts.
const immediateTouch = { api: null, actions: null };

function endImmediateTouch() {
  window.removeEventListener('touchmove', onImmediateMove, { capture: true });
  window.removeEventListener('touchend', onImmediateEnd, { capture: true });
  window.removeEventListener('touchcancel', onImmediateCancel, { capture: true });
  const { actions } = immediateTouch;
  immediateTouch.actions = null;
  return actions;
}
const touchPoint = (e) => ({ x: e.touches[0].clientX, y: e.touches[0].clientY });
function onImmediateMove(e) {
  if (!immediateTouch.actions || !e.touches.length) return;
  e.preventDefault();
  immediateTouch.actions.move(touchPoint(e));
}
function onImmediateEnd() {
  endImmediateTouch()?.drop();
}
function onImmediateCancel() {
  endImmediateTouch()?.cancel();
}
function onImmediateStart(e) {
  const { api } = immediateTouch;
  if (!api || immediateTouch.actions || e.touches.length !== 1) return;
  const handle = e.target.closest?.('[data-rfd-drag-handle-draggable-id]');
  if (!handle) return;
  const preDrag = api.tryGetLock(handle.getAttribute('data-rfd-drag-handle-draggable-id'), () => {
    // lock released by the library
    endImmediateTouch();
  });
  if (!preDrag) return;
  immediateTouch.actions = preDrag.fluidLift(touchPoint(e));
  window.addEventListener('touchmove', onImmediateMove, { capture: true, passive: false });
  window.addEventListener('touchend', onImmediateEnd, { capture: true });
  window.addEventListener('touchcancel', onImmediateCancel, { capture: true });
}
if (typeof window !== 'undefined') {
  window.addEventListener('touchstart', onImmediateStart, { capture: true, passive: true });
}

function useImmediateTouchSensor(api) {
  useEffect(() => {
    immediateTouch.api = api;
    return () => {
      endImmediateTouch()?.cancel();
      immediateTouch.api = null;
    };
  }, [api]);
}

function ListDetail() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [frequentContacts, setFrequentContacts] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [eventsToShare, setEventsToShare] = useState([]);
  const [events, setEvents] = useState([]);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [editItemDesc, setEditItemDesc] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [animatingItems, setAnimatingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moveDialogItem, setMoveDialogItem] = useState(null);
  const [moveTargets, setMoveTargets] = useState([]);
  const [copyInstead, setCopyInstead] = useState(false);


  useEffect(() => {
    fetchList(true);
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (shareDialogOpen) {
      fetchFrequentContacts();
    }
  }, [shareDialogOpen]);

  const fetchList = async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }
    try {
      const response = await api.get(`/lists/${id}`);
      setList(response.data);
      setItems(response.data.items);
      setEditedName(response.data.name || '');
    } catch (error) {
      console.error('Error fetching list:', error);
      enqueueSnackbar('Failed to fetch list details.', { variant: 'error' });
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };
  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      enqueueSnackbar('Failed to fetch events.', { variant: 'error' });
    }
  };
  const fetchFrequentContacts = async () => {
    try {
      const response = await api.get('/users/frequent-contacts');
      setFrequentContacts(response.data.frequentContacts);
    } catch (error) {
      console.error('Error fetching frequent contacts:', error);
    }
  };

  if (loading) {
    return <LoadingState message="Loading list details..." minHeight={400} />;
  }

  if (!list) {
    return (
      <Box>
        <Typography variant="h6">Unable to load this list right now.</Typography>
      </Box>
    );
  }

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (newItemDesc.trim() === '') return;
    try {
      await api.post(`/lists/${id}/items`, { shortDescription: newItemDesc });
      setNewItemDesc('');
      fetchList();
      enqueueSnackbar('Item added successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error adding item:', error);
      enqueueSnackbar('Failed to add item.', { variant: 'error' });
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/lists/${id}/items/${itemId}`);
      fetchList();
      enqueueSnackbar('Item deleted successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting item:', error);
      enqueueSnackbar('Failed to delete item.', { variant: 'error' });
    }
  };

  const handleEditItem = (item) => {
    setCurrentItem(item);
    setEditItemDesc(item.shortDescription);
    setEditItemDialogOpen(true);
  };

  const handleUpdateItem = async () => {
    if (editItemDesc.trim() === '') return;
    try {
      await api.put(`/lists/${id}/items/${currentItem._id}`, { shortDescription: editItemDesc });
      setEditItemDialogOpen(false);
      setCurrentItem(null);
      setEditItemDesc('');
      fetchList();
      enqueueSnackbar('Item updated successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error updating item:', error);
      enqueueSnackbar('Failed to update item.', { variant: 'error' });
    }
  };

  const handleShareList = async () => {

    try {
      await api.post(`/lists/${id}/share`, {
        emails: [selectedEmail.trim().toLowerCase()],
        groupIds: eventsToShare.map((event) => event._id),
      });
      setShareDialogOpen(false);
      setEventsToShare([]);
      fetchList();
      enqueueSnackbar('List shared successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error sharing list:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to share list.', { variant: 'error' });
    }
  };


  const handleUnshare = async (shareItem) => {
    try {
      if (shareItem.kind === 'User') {
        await api.post(`/lists/${id}/unshare`, { email: shareItem.item.email });
      } else if (shareItem.kind === 'Group') {
        await api.post(`/lists/${id}/unshare`, { groupId: shareItem.item._id });
      }
      fetchList();
      enqueueSnackbar('List unshared successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Error unsharing list:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to unshare list.', { variant: 'error' });
    }
  };


  // Handle name edit toggle
  const handleNameDoubleClick = () => {
    setEditedName(list.name || '');
    setIsEditingName(true);
  };

  const handleNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleNameSave = async () => {
    if (editedName.trim() === '') {
      enqueueSnackbar('Name cannot be empty.', { variant: 'warning' });
      return;
    }
    try {
      const response = await api.put(`/lists/${id}`, { name: editedName });
      setList(response.data);
      setEditedName(response.data.name || '');
      setIsEditingName(false);
      enqueueSnackbar('List name updated.', { variant: 'success' });
    } catch (error) {
      console.error('Error updating list name:', error);
      enqueueSnackbar('Failed to update list name.', { variant: 'error' });
    }
  };

  const handleNameCancel = () => {
    setEditedName(list.name);
    setIsEditingName(false);
  };

  // Handle complete action
  const handleCompleteItem = async (itemId) => {
    try {
      // Add the item to animatingItems
      setAnimatingItems((prev) => [...prev, itemId]);

      // Delay the completion to allow the animation to play
      setTimeout(async () => {
        await api.post(`/items/${itemId}/complete`);
        setItems((prevItems) =>
          prevItems.map((item) =>
            item._id === itemId ? { ...item, completedAt: new Date() } : item
          )
        );
        // Remove the item from animatingItems
        setAnimatingItems((prev) => prev.filter((id) => id !== itemId));
      }, 500); // Duration matches the CSS animation duration
    } catch (error) {
      console.error('Error completing item:', error);
      enqueueSnackbar('Failed to complete item.', { variant: 'error' });
      // Remove the item from animatingItems in case of error
      setAnimatingItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  // Handle uncomplete action
  const handleUncompleteItem = async (itemId) => {
    try {
      await api.post(`/items/${itemId}/uncomplete`);
      setItems((prevItems) =>
        prevItems.map((item) =>
          item._id === itemId ? { ...item, completedAt: null } : item
        )
      );
    } catch (error) {
      console.error('Error uncompleting item:', error);
      enqueueSnackbar('Failed to uncomplete item.', { variant: 'error' });
    }
  };

  // Persist a new order of the uncompleted items. Completed items keep their
  // existing positions in the underlying array; the server requires the full set.
  const saveItemOrder = async (newUncompleted) => {
    const previousItems = items;
    let next = 0;
    const merged = items.map((item) => (item.completedAt ? item : newUncompleted[next++]));
    setItems(merged);
    try {
      await api.put(`/lists/${id}/items/reorder`, { itemIds: merged.map((item) => item._id) });
    } catch (error) {
      console.error('Error reordering items:', error);
      setItems(previousItems);
      enqueueSnackbar('Failed to reorder items.', { variant: 'error' });
      fetchList(); // the list may have changed under us (e.g. another user's edit)
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(uncompletedItems);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    saveItemOrder(reordered);
  };

  const handleOpenMoveDialog = async (item) => {
    setMoveDialogItem(item);
    setCopyInstead(false);
    try {
      const response = await api.get('/lists?includeItems=false');
      setMoveTargets(response.data.filter((l) => l.isOwner && l._id !== id));
    } catch (error) {
      console.error('Error fetching lists:', error);
      setMoveDialogItem(null);
      enqueueSnackbar('Failed to load your lists.', { variant: 'error' });
    }
  };

  const handleMoveItem = async (targetList) => {
    const item = moveDialogItem;
    const copy = copyInstead;
    setMoveDialogItem(null);
    try {
      await api.post(`/lists/${id}/items/${item._id}/move`, { targetListId: targetList._id, copy });
      if (!copy) {
        setItems((prev) => prev.filter((i) => i._id !== item._id));
      }
      enqueueSnackbar(`${copy ? 'Copied' : 'Moved'} to "${targetList.name}".`, { variant: 'success' });
    } catch (error) {
      console.error(`Error ${copy ? 'copying' : 'moving'} item:`, error);
      enqueueSnackbar(error.response?.data?.message || `Failed to ${copy ? 'copy' : 'move'} item.`, {
        variant: 'error',
      });
    }
  };

  // Split items into completed and uncompleted
  const uncompletedItems = items.filter((item) => !item.completedAt);
  const completedItems = items
    .filter((item) => item.completedAt)
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)); // Most recent first

  // Copy the list name and its active items, in their current order, as Markdown.
  // Continuation lines of multi-line items are indented so they stay in the bullet.
  const handleCopyAsMarkdown = async () => {
    const bullets = uncompletedItems
      .map((item) => `- ${item.shortDescription.trim().split('\n').join('\n  ')}`)
      .join('\n');
    const markdown = `# ${list.name}\n\n${bullets}`;
    try {
      await navigator.clipboard.writeText(markdown);
      enqueueSnackbar('List copied as Markdown.', { variant: 'success' });
    } catch (error) {
      console.error('Error copying list:', error);
      enqueueSnackbar('Failed to copy list.', { variant: 'error' });
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 0 },
        py: 1,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      {list && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignSelf="stretch"
          >
            {/* Editable List Name */}
            {isEditingName ? (
              <Box display="flex" alignItems="center">
                <TextField
                  value={editedName}
                  onChange={handleNameChange}
                  variant="outlined"
                  size="small"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleNameSave();
                    }
                  }}
                />
                <IconButton onClick={handleNameSave} color="primary">
                  <Save />
                </IconButton>
                <IconButton onClick={handleNameCancel} color="secondary">
                  <Cancel />
                </IconButton>
              </Box>
            ) : (
              <Typography
                variant="h5"
                gutterBottom
                onDoubleClick={handleNameDoubleClick}
                sx={{
                  cursor: 'pointer',
                  wordBreak: 'break-word',
                  textAlign: { xs: 'center', sm: 'left' },
                  width: '100%',
                  fontFamily: '"Fraunces", Georgia, serif',
                }}
              >
                {list.name}
              </Typography>
            )}
          </Box>

          {/* Owner and Sharing Information */}
          <Box sx={{ mt: 2, mb: 2 }}>
            {list.owner && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Owner:
                </Typography>
                <Chip
                  label={list.owner.email || 'You'}
                  size="small"
                  color={list.isOwner ? 'primary' : 'default'}
                />
              </Box>
            )}

            {list.sharedWith && list.sharedWith.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Shared with:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {list.sharedWith.map((share, index) => (
                    <Chip
                      key={index}
                      label={
                        share.kind === 'User'
                          ? share.item.email
                          : share.item.name
                      }
                      size="small"
                      color={share.kind === 'User' ? 'secondary' : 'primary'}
                      onDelete={() => handleUnshare(share)}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center">
            <Button variant="outlined" sx={{ ml: 0, mb: 1 }} onClick={() => setShareDialogOpen(true)}>
              Share List
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              sx={{ ml: 1, mb: 1 }}
              onClick={handleCopyAsMarkdown}
              disabled={uncompletedItems.length === 0}
            >
              Copy as Markdown
            </Button>
          </Box>

          {/* Form to add new item */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: 'rgba(253,250,245,0.6)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
            }}
          >
            <form onSubmit={handleAddItem}>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="New Item"
                    value={newItemDesc}
                    onChange={(e) => setNewItemDesc(e.target.value)}
                    multiline
                    minRows={1}
                    variant="outlined"
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddItem(e);
                      }
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Add />}
                    fullWidth
                    sx={{ height: '100%' }}
                    size="small"
                  >
                    Add Item
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Items Container */}
          <Box mt={2}>
            {/* Uncompleted Items */}
            <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>Items</Typography>
            {uncompletedItems.length === 0 ? (
              <Typography sx={{ fontStyle: 'italic' }}>No items found.</Typography>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd} sensors={[useImmediateTouchSensor]}>
                <Droppable droppableId="uncompleted-items">
                  {(dropProvided) => (
                    <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                      {uncompletedItems.map((item, index) => {
                        const isAnimating = animatingItems.includes(item._id);

                        return (
                          <Draggable key={item._id} draggableId={item._id} index={index}>
                            {(dragProvided, snapshot) => (
                              <Paper
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                sx={{
                                  p: 1,
                                  mb: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 3,
                                  ...(snapshot.isDragging && { boxShadow: 6 }),
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                                    <Box
                                      {...dragProvided.dragHandleProps}
                                      aria-label="Drag to reorder"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 36,
                                        height: 36,
                                        ml: -0.5,
                                        color: 'text.disabled',
                                        touchAction: 'none',
                                        cursor: 'grab',
                                      }}
                                    >
                                      <DragIndicator fontSize="small" />
                                    </Box>
                                    {/* Item Details with Conditional Strikethrough Animation */}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        wordBreak: 'break-word',
                                        pr: 1,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        display: 'inline-block',
                                        ...(isAnimating && {
                                          '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: 'secondary.main',
                                            top: '50%',
                                            transform: 'translateY(-50%) translateX(-100%)',
                                            animation: 'strikethroughAnim 0.5s forwards',
                                          },
                                        }),
                                      }}
                                    >
                                      <LinkifiedText text={item.shortDescription} />
                                    </Typography>
                                  </Box>
                                  {/* Action Icons */}
                                  <Box sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton
                                      onClick={() => handleCompleteItem(item._id)}
                                      sx={{ color: 'secondary.main' }}
                                      disabled={isAnimating} // Disable button during animation
                                      size="small"
                                    >
                                      <Done fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => handleOpenMoveDialog(item)}
                                      aria-label="Move or copy to another list"
                                      size="small"
                                    >
                                      <DriveFileMove fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={() => handleEditItem(item)} size="small">
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Paper>
                            )}
                          </Draggable>
                        );
                      })}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* Completed Items */}
            <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Fraunces", Georgia, serif' }}>Completed Items</Typography>
            {completedItems.length === 0 ? (
              <Typography sx={{ fontStyle: 'italic' }}>No completed items.</Typography>
            ) : (
              completedItems.map((item) => (
                <Paper
                  key={item._id}
                  sx={{
                    p: 1,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* Item Details with Static Strikethrough */}
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        textDecoration: 'line-through',
                        textDecorationColor: '#52B788',
                        wordBreak: 'break-word',
                        pr: 1,
                        color: 'text.disabled',
                      }}
                    >
                      <LinkifiedText text={item.shortDescription} />
                    </Typography>
                    {/* Action Icons */}
                    <Box>
                      <IconButton
                        onClick={() => handleUncompleteItem(item._id)}
                        sx={{ color: 'secondary.main' }}
                        size="small"
                      >
                        <Undo fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteItem(item._id)}
                        color="error"
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>

          {/* Edit Item Dialog */}
          <Dialog
            open={editItemDialogOpen}
            onClose={() => setEditItemDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Edit Item</DialogTitle>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateItem();
              }}
            >
              <DialogContent>
                <TextField
                  label="Item Description"
                  value={editItemDesc}
                  onChange={(e) => setEditItemDesc(e.target.value)}
                  multiline
                  minRows={1}
                  variant="outlined"
                  fullWidth
                  autoFocus
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditItemDialogOpen(false)}>Cancel</Button>
                <Button type="submit" variant="contained">
                  Update
                </Button>
              </DialogActions>
            </form>
          </Dialog>

          {/* Move Item Dialog */}
          <Dialog
            open={Boolean(moveDialogItem)}
            onClose={() => setMoveDialogItem(null)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>{copyInstead ? 'Copy to another list' : 'Move to another list'}</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                The item will be added to the top of the list you choose.
              </Typography>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    checked={copyInstead}
                    onChange={(e) => setCopyInstead(e.target.checked)}
                    size="small"
                  />
                }
                label="Copy instead (keep it in this list too)"
              />
              {moveTargets.length === 0 ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  You have no other lists to move this item to.
                </Typography>
              ) : (
                moveTargets.map((target) => (
                  <Button
                    key={target._id}
                    fullWidth
                    onClick={() => handleMoveItem(target)}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    {target.name}
                  </Button>
                ))
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMoveDialogItem(null)}>Cancel</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog for sharing the list */}
          <Dialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Share List</DialogTitle>
            <DialogContent>
              <Box mt={1} mb={1}>
                <Typography variant="subtitle1">Shared With:</Typography>
              </Box>
              <Box display="flex" flexWrap="wrap">
                {list.sharedWith && list.sharedWith.length > 0 ? (
                  list.sharedWith.map((shareItem, index) => (
                    <Chip
                      key={index}
                      label={shareItem.item.name || shareItem.item.email}
                      color={shareItem.kind === 'User' ? 'secondary' : 'primary'}
                      onDelete={() => handleUnshare(shareItem)}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Not shared
                  </Typography>
                )}
              </Box>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={frequentContacts}
                    onInputChange={(event, value) => setSelectedEmail(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Recipient Email" variant="outlined" />
                    )}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1, fontStyle: 'italic' }}
                  >
                    People you share a list with become co-owners. They can add, delete,
                    and complete items with you, as well as rename or delete the list
                    itself.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    options={events}
                    getOptionLabel={(option) => option.name}
                    value={eventsToShare}
                    onChange={(event, inputValue) => {
                      setEventsToShare(inputValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.name}
                          {...getTagProps({ index })}
                          key={option._id}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Events to Share With"
                        placeholder="Select events"
                      />
                    )}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1, fontStyle: 'italic' }}
                  >
                    Events you share a list with will have read-only access. They can view
                    your list and purchase items off the list without you being notified.
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleShareList} variant="contained">
                Share
              </Button>
            </DialogActions>
          </Dialog>


        </>
      )}
    </Box>

  );
}

export default ListDetail;
